import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import { Icon } from 'Components/UI/Icon';
import { Skeleton } from 'antd';
import { RateioModeEnum } from 'Data/interfaces/Faturamento/IRateio';
import { numberToMoney } from 'Utils/Money';
import { formatWeight } from 'Utils/RateioUtils';
import BillingSplitModeSelector from '../SplitModeSelector';
import TermsStatus from 'Pages/Faturamento/Components/TermsStatus';
import SpeFormModal from '../SpeFormModal';
import { ModalBody } from '../ModalBody';
import { useRateio } from '../../useRateio';
import {
  Actions,
  AdjustChip,
  Amount,
  Card,
  CardHeader,
  CardTitle,
  EmptyDescription,
  EmptyState,
  EmptyTitle,
  Footer,
  Grid,
  HeaderActions,
  Message,
  MeterLabel,
  MeterTop,
  MeterValue,
  MeterWrapper,
  Percent,
  PlanBox,
  PlanLabel,
  PlanValue,
  RemoveConfirmHint,
  RemoveConfirmText,
  RowAction,
  RowActions,
  Segment,
  SpeDocument,
  SpeName,
  TermsLabel,
  TermsLine,
  Table,
  Td,
  Th,
  TotalLabel,
  TotalRow,
  TotalValue,
  Track,
  WeightField,
  WeightInput,
} from '../../styles';

interface IRateioEditor {
  // Sem onCancel, o botão Cancelar não aparece (no /adm, fechar é pela modal).
  onCancel?: () => void;
  // Dentro de outra superfície (aba do /adm): sem o respiro lateral da página.
  embedded?: boolean;
}

// Editor do rateio: modo de divisão, pesos por SPE e cadastro/remoção de SPEs.
// Opera no tenant do RateioScope — o do usuário na página /faturamento/rateio, ou
// o tenant selecionado na aba de rateio do painel /adm (PBI 12501).
const RateioEditor: FC<IRateioEditor> = ({ onCancel, embedded }) => {
  const { t } = useTranslation(['faturamento', 'common']);
  const {
    rows,
    mode,
    total,
    totalAmount,
    difference,
    differenceAmount,
    isComplete,
    hasInvalidInput,
    isLoading,
    isSaving,
    planPrice,
    hasSpes,
    headquartersTerms,
    resendingSpeId,
    onResendTerms,
    isSpeFormOpen,
    speBeingEdited,
    isSavingSpe,
    speToRemove,
    isRemoving,
    onModeChange,
    onWeightChange,
    onSave,
    onAddSpe,
    onEditSpe,
    onCloseSpeForm,
    onSubmitSpe,
    onRequestRemoveSpe,
    onCancelRemoveSpe,
    onConfirmRemoveSpe,
  } = useRateio();

  const state: 'ok' | 'over' | 'under' = isComplete
    ? 'ok'
    : total > 100
      ? 'over'
      : 'under';

  // O excedente vira segmento próprio em vermelho: o erro fica visível na barra
  // antes de o número ser lido.
  const segments = useMemo(() => {
    const result: { key: string; width: number; over?: boolean }[] = [];
    let used = 0;

    rows.forEach((row) => {
      const weight = row.weight ?? 0;
      const fits = Math.max(0, Math.min(weight, 100 - used));
      if (fits > 0) result.push({ key: row.speId, width: fits });

      const excess = weight - fits;
      if (excess > 0) result.push({ key: `${row.speId}-over`, width: excess, over: true });

      used += weight;
    });

    return result;
  }, [rows]);

  const message = useMemo(() => {
    if (hasInvalidInput) return t('faturamento:rateio.invalidWeight');
    if (isComplete) return t('faturamento:rateio.messageComplete');

    const amount = differenceAmount ? numberToMoney(differenceAmount, 'real') : '';

    return difference > 0
      ? t('faturamento:rateio.messageMissing', {
          percent: formatWeight(difference),
          amount,
        })
      : t('faturamento:rateio.messageExceeding', {
          percent: formatWeight(Math.abs(difference)),
          amount,
        });
  }, [hasInvalidInput, isComplete, difference, differenceAmount, t]);

  const isUniform = mode === RateioModeEnum.Uniform;

  return (
    <>
      <Grid $embedded={embedded}>
        <Card>
          {isLoading ? (
            <Skeleton active />
          ) : (
            <>
              <CardHeader>
                <CardTitle>{t('faturamento:rateio.cardTitle')}</CardTitle>
                <HeaderActions>
                  {planPrice ? (
                    <PlanBox>
                      <PlanLabel>{t('faturamento:rateio.planLabel')}</PlanLabel>
                      <PlanValue>{numberToMoney(planPrice, 'real')}</PlanValue>
                    </PlanBox>
                  ) : null}
                  {hasSpes ? (
                    <Button type="primary" onClick={onAddSpe}>
                      {t('faturamento:spe.addButton')}
                    </Button>
                  ) : null}
                </HeaderActions>
              </CardHeader>

              {!hasSpes ? (
                <EmptyState>
                  <EmptyTitle>{t('faturamento:spe.emptyTitle')}</EmptyTitle>
                  <EmptyDescription>
                    {t('faturamento:spe.emptyDescription')}
                  </EmptyDescription>
                  <Button type="primary" onClick={onAddSpe}>
                    {t('faturamento:spe.addFirstButton')}
                  </Button>
                </EmptyState>
              ) : (
              <>
              <BillingSplitModeSelector value={mode} onChange={onModeChange} />

              {/* Com o rateio, a matriz e cada SPE dão ciência nos Termos de Uso
                  (corresponsabilidade pelo pagamento). Só sinaliza, não bloqueia. */}
              <TermsLine>
                <TermsLabel>{t('faturamento:terms.headquartersLabel')}</TermsLabel>
                <TermsStatus
                  terms={headquartersTerms}
                  isResending={resendingSpeId === 'headquarters'}
                  onResend={() => onResendTerms()}
                />
              </TermsLine>

              <MeterWrapper>
                <MeterTop>
                  <MeterLabel>{t('faturamento:rateio.distribution')}</MeterLabel>
                  <MeterValue $state={state}>
                    {t('faturamento:rateio.distributed', {
                      total: formatWeight(total),
                    })}
                  </MeterValue>
                </MeterTop>
                <Track>
                  {segments.map((segment) => (
                    <Segment
                      key={segment.key}
                      $width={segment.width}
                      $over={segment.over}
                    />
                  ))}
                </Track>
              </MeterWrapper>

              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: '42%' }}>{t('faturamento:rateio.columnSpe')}</Th>
                    <Th $right style={{ width: '20%' }}>
                      {t('faturamento:rateio.columnWeight')}
                    </Th>
                    <Th $right style={{ width: '26%' }}>
                      {t('faturamento:rateio.columnAmount')}
                    </Th>
                    <Th $right style={{ width: '12%' }}>
                      {t('faturamento:spe.columnActions')}
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.speId}>
                      <Td>
                        <SpeName>{row.name}</SpeName>
                        <SpeDocument>{row.document}</SpeDocument>
                        <TermsStatus
                          terms={row.terms}
                          isResending={resendingSpeId === row.speId}
                          onResend={() => onResendTerms(row.speId)}
                        />
                      </Td>
                      <Td $right>
                        <WeightField>
                          <WeightInput
                            value={row.input}
                            readOnly={isUniform}
                            $invalid={row.weight === null}
                            inputMode="decimal"
                            aria-label={t('faturamento:rateio.weightAriaLabel', {
                              name: row.name,
                            })}
                            onChange={(event) =>
                              onWeightChange(row.speId, event.target.value)
                            }
                          />
                          <Percent>%</Percent>
                          {row.isRoundingAdjusted ? (
                            <AdjustChip>{t('faturamento:rateio.adjustChip')}</AdjustChip>
                          ) : null}
                        </WeightField>
                      </Td>
                      <Td $right>
                        <Amount>
                          {row.amount !== undefined
                            ? numberToMoney(row.amount, 'real')
                            : '—'}
                        </Amount>
                      </Td>
                      <Td $right>
                        <RowActions>
                          <RowAction
                            type="button"
                            title={t('faturamento:spe.editAction', {
                              name: row.name,
                            })}
                            aria-label={t('faturamento:spe.editAction', {
                              name: row.name,
                            })}
                            onClick={() => onEditSpe(row.speId)}
                          >
                            <Icon icon="editar" customSize={15} />
                          </RowAction>
                          <RowAction
                            type="button"
                            $danger
                            title={t('faturamento:spe.removeAction', {
                              name: row.name,
                            })}
                            aria-label={t('faturamento:spe.removeAction', {
                              name: row.name,
                            })}
                            onClick={() => onRequestRemoveSpe(row.speId)}
                          >
                            <Icon icon="excluir" customSize={15} />
                          </RowAction>
                        </RowActions>
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td>
                      <TotalLabel>{t('faturamento:rateio.total')}</TotalLabel>
                    </Td>
                    <Td $right>
                      <TotalValue $invalid={!isComplete}>
                        {`${formatWeight(total)}%`}
                      </TotalValue>
                    </Td>
                    <Td $right>
                      <TotalValue $invalid={!isComplete}>
                        {totalAmount !== undefined
                          ? numberToMoney(totalAmount, 'real')
                          : '—'}
                      </TotalValue>
                    </Td>
                    <Td />
                  </TotalRow>
                </tfoot>
              </Table>

              <Footer>
                <Message $state={state}>{message}</Message>
                <Actions>
                  {onCancel ? (
                    <Button type="text" onClick={onCancel}>
                      {t('common:cancel')}
                    </Button>
                  ) : null}
                  <Button
                    type="primary"
                    onClick={onSave}
                    disabled={!isComplete}
                    loading={isSaving}
                  >
                    {t('faturamento:rateio.save')}
                  </Button>
                </Actions>
              </Footer>
              </>
              )}
            </>
          )}
        </Card>
      </Grid>

      {/* Montado só quando aberto: desmontar ao fechar garante que o próximo
          cadastro abra em branco em vez de herdar os valores da SPE anterior. */}
      {isSpeFormOpen ? (
        <SpeFormModal
          visible
          spe={speBeingEdited}
          isSaving={isSavingSpe}
          onSubmit={onSubmitSpe}
          onCancel={onCloseSpeForm}
        />
      ) : null}

      <Modal
        title={t('faturamento:spe.removeTitle')}
        visible={Boolean(speToRemove)}
        width={460}
        onCancel={onCancelRemoveSpe}
        footerButtons={
          <>
            <Button type="text" onClick={onCancelRemoveSpe} disabled={isRemoving}>
              {t('common:cancel')}
            </Button>
            <Button
              type="primary"
              onClick={onConfirmRemoveSpe}
              loading={isRemoving}
            >
              {t('faturamento:spe.removeConfirm')}
            </Button>
          </>
        }
      >
        <ModalBody>
          {/* Nome interpolado como texto, não como HTML: CompanyName é digitado
              pelo usuário e cairia direto no DOM. */}
          <RemoveConfirmText>
            {t('faturamento:spe.removeQuestion', {
              name: speToRemove?.CompanyName ?? '',
            })}
          </RemoveConfirmText>
          <RemoveConfirmHint>
            {mode === RateioModeEnum.Uniform
              ? t('faturamento:spe.removeHintUniform')
              : t('faturamento:spe.removeHintCustom')}
          </RemoveConfirmHint>
        </ModalBody>
      </Modal>
    </>
  );
};

export default RateioEditor;
